import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Button,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Services', path: '/services' },
    { label: 'Breach Check', path: '/breach-check' },
    { label: 'Identity Recovery', path: '/identity-recovery' },
    { label: 'Security Hub', path: '/security-hub' },
    { label: 'Security News', path: '/news' },
    { label: 'Dev Tools', path: '/tools-development' },
    { label: 'About', path: '/about' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="sticky" elevation={2} sx={{ backgroundColor: 'primary.main' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo */}
            <Box
              component="img"
              src="/assets/virtus-logo.png"
              alt="Smart Industries LLC"
              onClick={() => navigate('/')}
              sx={{ 
                height: 45,
                width: 45,
                mr: 2,
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'secondary.main',
                cursor: 'pointer',
                objectFit: 'cover',
              }}
            />
            
            <Typography 
              variant="h6" 
              component="div" 
              onClick={() => navigate('/')}
              sx={{ 
                flexGrow: { xs: 1, md: 0 }, 
                fontWeight: 600,
                cursor: 'pointer',
                mr: 4
              }}
            >
              Smart Industries LLC
            </Typography>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      color: 'white',
                      fontWeight: isActive(item.path) ? 700 : 500,
                      borderBottom: isActive(item.path) ? '2px solid' : 'none',
                      borderColor: 'secondary.main',
                      borderRadius: 0,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile Menu */}
            {isMobile && (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  onClick={handleMenuOpen}
                  sx={{ ml: 'auto' }}
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: { width: 250 }
                  }}
                >
                  {navItems.map((item) => (
                    <MenuItem
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      selected={isActive(item.path)}
                      sx={{
                        fontWeight: isActive(item.path) ? 700 : 400,
                      }}
                    >
                      {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* Contact Us */}
            {!isMobile && (
              <Button
                variant="outlined"
                onClick={() => handleNavigation('/about')}
                sx={{
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'secondary.light',
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                  },
                }}
              >
                Contact Us
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: '#0d3b66',
          color: 'white',
          py: 4,
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Smart Industries LLC
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                We work hard, so you can live Smart!
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/services')}>
                  Software Solutions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/services')}>
                  Hardware Support
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/services')}>
                  Training & Education
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Security Tools
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/breach-check')}>
                  PII Breach Monitor
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/identity-recovery')}>
                  Identity Recovery
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, cursor: 'pointer' }} onClick={() => navigate('/news')}>
                  Security News
                </Typography>
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Contact
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Orlando, FL
                </Typography>
                <Typography 
                  variant="body2" 
                  component="a"
                  href="mailto:mikel.smart@smartindustriesllc.com"
                  sx={{ opacity: 0.9, color: 'inherit', textDecoration: 'none' }}
                >
                  mikel.smart@smartindustriesllc.com
                </Typography>
                <Typography 
                  variant="body2" 
                  component="a"
                  href="tel:561-344-4801"
                  sx={{ opacity: 0.9, color: 'inherit', textDecoration: 'none' }}
                >
                  561-344-4801
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', mt: 4, pt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                sx={{ opacity: 0.7, cursor: 'pointer', '&:hover': { opacity: 1 } }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.7, cursor: 'pointer', '&:hover': { opacity: 1 } }}
                onClick={() => navigate('/terms-of-service')}
              >
                Terms of Service
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.7, cursor: 'pointer', '&:hover': { opacity: 1 } }}
                onClick={() => navigate('/data-deletion')}
              >
                Data Deletion
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                © {new Date().getFullYear()} Smart Industries LLC. All rights reserved.
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7, fontStyle: 'italic', display: 'block', mt: 1 }}>
                Virtus Pre Nummis
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AppLayout;
